// src/pages/mc/demand-executions-legacy.ts
//
// ⚠️ MÓDULO LEGACY — REMOVER PÓS-RESTART DO BACKEND.
//
// Correção UX v2 W1.5: o DemandDetailPanel passou a consumir
// GET /v1/demands/:id/executions (api.getDemandExecutions). Enquanto o
// backend não reinicia com a rota nova, o endpoint responde 404 — este módulo
// é o fallback funcional com as queries supabase.from() ANTIGAS do painel
// (P1.11.E/P1.13 F2a/F6/W3.3/W3.5), normalizadas pro MESMO shape do endpoint
// ({ tasks, executions: DemandExecutionRow[] }).
//
// É o ÚNICO lugar do caminho de demandas com supabase.from() de leitura.
// Quando o restart acontecer e o endpoint estiver no ar, deletar este arquivo
// e o branch de fallback do DemandDetailPanel.

import { supabase } from '@/lib/supabase';
import type { BackendTask, Demand } from '@/lib/api';
import type { DemandExecutionRow } from './demand-metrics';

interface RawExecRow {
  id: string;
  task_id?: string | null;
  agent_execution_id?: string | null;
  parent_task_id?: string | null;
  standalone_task_id?: string | null;
  handler_name?: string | null;
  agent_id?: string | null;
  tool_slug?: string | null;
  skill_id?: string | null;
  status: string | null;
  error?: unknown;
  error_message?: string | null;
  started_at: string | null;
  completed_at?: string | null;
  ended_at?: string | null;
  tokens_input: number | null;
  tokens_output: number | null;
  cost_usd: number | null;
  duration_ms: number | null;
  cache_creation_input_tokens?: number | null;
  cache_read_input_tokens?: number | null;
}

function errToString(err: unknown, errMsg?: string | null): string | null {
  if (errMsg && typeof errMsg === 'string' && errMsg.trim()) return errMsg.slice(0, 300);
  if (!err) return null;
  if (typeof err === 'string') return err.slice(0, 300);
  try {
    return JSON.stringify(err).slice(0, 300);
  } catch {
    return null;
  }
}

function normalize(kind: DemandExecutionRow['kind'], r: RawExecRow): DemandExecutionRow {
  return {
    id: r.id,
    kind,
    status: r.status,
    started_at: r.started_at,
    finished_at: r.completed_at ?? r.ended_at ?? null,
    duration_ms: r.duration_ms,
    cost_usd: r.cost_usd,
    tokens_input: r.tokens_input,
    tokens_output: r.tokens_output,
    error: errToString(r.error, r.error_message),
    handler_name: r.handler_name ?? null,
    skill_id: r.skill_id ?? null,
    agent_id: r.agent_id ?? null,
    task_id: r.task_id ?? r.parent_task_id ?? r.standalone_task_id ?? null,
    trace_id: null,
    // tool_executions expõe o slug da tool — vira o alvo da linha na Atividade.
    target_slug: r.tool_slug ?? null,
    cache_creation_input_tokens: r.cache_creation_input_tokens ?? null,
    cache_read_input_tokens: r.cache_read_input_tokens ?? null,
  };
}

export interface LegacyDemandExecutions {
  tasks: BackendTask[];
  executions: DemandExecutionRow[];
}

/**
 * Fallback pré-restart (404 no endpoint novo): reconstrói tasks + execuções
 * da demanda direto das tabelas, com as MESMAS regras de cascateamento do
 * painel antigo:
 *   - tasks: demand_id ∪ root_trace_id == demand.trace_id (P1.13 F6);
 *   - handler_executions: demand_id (W3.3, idx_he_demand) ∪ task_id;
 *   - agent_executions: task_id (demand_id não populado lá);
 *   - tool_executions: agent_execution_id (cascateia de agent_executions);
 *   - skill_executions: parent_task_id ∪ standalone_task_id.
 */
export async function fetchDemandExecutionsLegacy(
  demand: Pick<Demand, 'id' | 'trace_id'>
): Promise<LegacyDemandExecutions> {
  const traceFilter = demand.trace_id
    ? `demand_id.eq.${demand.id},root_trace_id.eq.${demand.trace_id}`
    : `demand_id.eq.${demand.id}`;

  // Round 1a: tasks da demanda (união demand_id|root_trace_id, dedup por id).
  const tasksRes = await supabase
    .from('tasks')
    .select(
      'id,tenant_id,event_type,handler,status,target_slug,created_at,updated_at,started_at,completed_at,demand_id,pipeline_run_id,trace_id,root_trace_id,title,description,objective,duration_ms,sizing,retry_count,max_retries,agent_id,parent_task_id'
    )
    .or(traceFilter)
    .order('created_at', { ascending: true });
  const taskMap = new Map<string, BackendTask>();
  for (const t of (tasksRes.data ?? []) as BackendTask[]) taskMap.set(t.id, t);
  const tasks = [...taskMap.values()];
  const taskIds = tasks.map((t) => t.id);

  // Round 1b: handler (demand_id ∪ task_id) + agent (task_id) + skill
  // (parent ∪ standalone) — selects com métrica E status/erro num round só.
  const handlerSelect =
    'id,task_id,handler_name,status,error,started_at,completed_at,tokens_input,tokens_output,cost_usd,duration_ms';
  const skillSelect =
    'id,parent_task_id,standalone_task_id,skill_id,status,error,started_at,ended_at,tokens_input,tokens_output,cost_usd,duration_ms';
  const emptyRes = Promise.resolve({ data: [] as RawExecRow[] });

  const [heDemandRes, heTaskRes, aeRes, seParentRes, seStandaloneRes] = await Promise.all([
    supabase.from('handler_executions').select(handlerSelect).eq('demand_id', demand.id),
    taskIds.length > 0
      ? supabase.from('handler_executions').select(handlerSelect).in('task_id', taskIds)
      : emptyRes,
    taskIds.length > 0
      ? supabase
          .from('agent_executions')
          .select(
            'id,task_id,agent_id,status,error_message,started_at,completed_at,tokens_input,tokens_output,cost_usd,duration_ms,cache_creation_input_tokens,cache_read_input_tokens'
          )
          .in('task_id', taskIds)
      : emptyRes,
    taskIds.length > 0
      ? supabase.from('skill_executions').select(skillSelect).in('parent_task_id', taskIds)
      : emptyRes,
    taskIds.length > 0
      ? supabase.from('skill_executions').select(skillSelect).in('standalone_task_id', taskIds)
      : emptyRes,
  ]);

  // Round 2: tool_executions via agent_execution_id.
  const aeRows = (aeRes.data ?? []) as RawExecRow[];
  const agentExecIds = aeRows.map((r) => r.id);
  const teRes =
    agentExecIds.length > 0
      ? await supabase
          .from('tool_executions')
          .select(
            'id,agent_execution_id,tool_slug,status,error,started_at,completed_at,tokens_input,tokens_output,cost_usd,duration_ms'
          )
          .in('agent_execution_id', agentExecIds)
      : { data: [] as RawExecRow[] };

  // Dedup por kind:id (handler e skill podem vir das duas queries da união).
  const out = new Map<string, DemandExecutionRow>();
  const push = (kind: DemandExecutionRow['kind'], rows: RawExecRow[]) => {
    for (const r of rows) {
      const key = `${kind}:${r.id}`;
      if (!out.has(key)) out.set(key, normalize(kind, r));
    }
  };
  push('handler', (heDemandRes.data ?? []) as RawExecRow[]);
  push('handler', (heTaskRes.data ?? []) as RawExecRow[]);
  push('agent', aeRows);
  push('tool', (teRes.data ?? []) as RawExecRow[]);
  push('skill', (seParentRes.data ?? []) as RawExecRow[]);
  push('skill', (seStandaloneRes.data ?? []) as RawExecRow[]);

  return { tasks, executions: [...out.values()] };
}
